/*
 * MCC delivery info
 * "Ships from" badge and estimated delivery for the visitor's country.
 * Data: product metafield mcc.delivery (filled automatically from Printify by the MCC stock sync).
 * Settings: block "MCC Delivery info" in the product section (theme editor).
 */
(() => {
  if (customElements.get('mcc-delivery')) return;

  const EUROPE = new Set(
    'AD AL AT AX BA BE BG BY CH CY CZ DE DK EE ES FI FO FR GB GG GI GR HR HU IE IM IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS SE SI SK SM UA VA XK'.split(' ')
  );
  const OCEANIA = new Set(['AU', 'NZ']);

  // Is the visitor in the same shipping region as the origin?
  const sameRegion = (origin, country) => {
    if (!origin || !country) return false;
    if (EUROPE.has(origin)) return EUROPE.has(country);
    if (OCEANIA.has(origin)) return OCEANIA.has(country);
    return origin === country;
  };

  const isPreview = () => {
    const s = window.Shopify || {};
    return Boolean(s.designMode) || Boolean(s.theme && s.theme.role && s.theme.role !== 'main');
  };

  let countryPromise = null;

  // The store's default market country. Shopify reports it (and sets the localization cookie to it)
  // for every visitor who has not picked another country, so it says nothing about where they are.
  const DEFAULT_COUNTRY = 'US';

  // Visitor country: a country picked in the store's selector wins; otherwise Shopify's IP based detection.
  const detectCountry = () => {
    if (countryPromise) return countryPromise;
    countryPromise = (async () => {
      const s = window.Shopify || {};
      const current = s.country ? String(s.country).toUpperCase() : null;
      if (current && current !== DEFAULT_COUNTRY) return current;
      try {
        const cached = sessionStorage.getItem('mcc-country');
        if (cached) return cached;
      } catch (e) {}
      let country = null;
      try {
        const root = (s.routes && s.routes.root) || '/';
        const res = await fetch(`${root}browsing_context_suggestions.json`, { credentials: 'same-origin' });
        if (res.ok) {
          const json = await res.json();
          country = json && json.detected_values && json.detected_values.country && json.detected_values.country.handle;
        }
      } catch (e) {}
      if (country) {
        country = String(country).toUpperCase();
        try {
          sessionStorage.setItem('mcc-country', country);
        } catch (e) {}
      }
      return country || current;
    })();
    return countryPromise;
  };

  const transitDays = (data, country) => {
    let fallback = null;
    for (const group of data.t || []) {
      const [min, max, countries] = group;
      if (countries.includes(country)) return [min, max];
      if (countries.includes('*')) fallback = [min, max];
    }
    return fallback;
  };

  const addBusinessDays = (start, days) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return d;
  };

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (m, key) => (key in values ? values[key] : m));

  class MccDelivery extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    readJson(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    async render() {
      const data = this.readJson('[data-mcc-data]');
      const text = this.readJson('[data-mcc-text]') || {};
      text.badge = text.badge || 'Ships from {origin}';
      text.range = text.range || '{from} to {to}';
      text.days = text.days || '{min} to {max} business days';
      if (!data || !data.o) return;

      const params = new URLSearchParams(window.location.search);
      let country = null;
      if (isPreview()) country = (params.get('mcc_country') || this.dataset.previewCountry || '').toUpperCase() || null;
      if (!country) country = await detectCountry();

      const origin = String(data.o).toUpperCase();
      const match = sameRegion(origin, country);
      const badgeMode = this.dataset.badgeMode;
      const etaMode = this.dataset.etaMode;

      const showBadge = badgeMode === 'always' || (badgeMode === 'match' && match);
      const days = country ? transitDays(data, country) : null;
      const showEta = Boolean(days) && (etaMode === 'always' || (etaMode === 'match' && match));

      const originRow = this.querySelector('[data-mcc-origin]');
      const etaRow = this.querySelector('[data-mcc-eta]');
      const note = this.querySelector('[data-mcc-note]');

      if (originRow) {
        originRow.hidden = !showBadge;
        if (showBadge) {
          const names = text.names || {};
          let originName = names[origin];
          if (!originName && EUROPE.has(origin)) originName = names.EU;
          if (!originName) {
            try {
              originName = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(origin);
            } catch (e) {
              originName = origin;
            }
          }
          this.querySelector('[data-mcc-origin-text]').textContent = fill(text.badge, { origin: originName });
        }
      }

      if (etaRow) {
        etaRow.hidden = !showEta;
        if (showEta) {
          const pmin = Number(this.dataset.pmin || 0);
          const pmax = Number(this.dataset.pmax || 0);
          const extra = Number(this.dataset.extra || 0);
          const minDays = pmin + days[0];
          const maxDays = Math.max(minDays, pmax + days[1] + extra);
          const locale = document.documentElement.lang || 'en';

          let countryName = country;
          try {
            countryName = new Intl.DisplayNames([locale], { type: 'region' }).of(country) || country;
          } catch (e) {}
          let label = text.label || '';
          if (text.showCountry && text.countryText) label = `${label} ${fill(text.countryText, { country: countryName })}`;

          let value;
          if (this.dataset.etaFormat === 'days') {
            value = fill(text.days, { min: minDays, max: maxDays });
          } else {
            const opts = { month: 'short', day: 'numeric' };
            if (this.dataset.etaFormat === 'dates_weekday') opts.weekday = 'short';
            const fmt = new Intl.DateTimeFormat(locale, opts);
            const today = new Date();
            const from = fmt.format(addBusinessDays(today, minDays));
            const to = fmt.format(addBusinessDays(today, maxDays));
            value = from === to ? from : fill(text.range, { from, to });
          }
          this.querySelector('[data-mcc-eta-label]').textContent = label.trim();
          this.querySelector('[data-mcc-eta-value]').textContent = value;
        }
      }

      if (note) note.hidden = !(showBadge || showEta) || !note.textContent.trim();

      this.hidden = !(showBadge || showEta);
      if (!this.hidden) requestAnimationFrame(() => this.classList.add('is-ready'));
    }
  }

  customElements.define('mcc-delivery', MccDelivery);
})();
